import {
  Box,
  Button,
  Container,
  Grid2,
  Typography,
  IconButton,
  Snackbar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import AddIcon from "@mui/icons-material/Add";
import UnitCard from "./UnitCardPG";
import { useGetUnitTypesByPropertyQuery } from "../../features/unitTypes/unitTypesApiSlice";
import Spinner from "../Spinner";
import { useCallback, useEffect, useState } from "react";
import {
  useDeleteUnitMutation,
  useGetUnitsQuery,
  useToggleStatusMutation,
  useUpdateUnitMutation,
} from "../../features/units/api/unitsApiSlice";
import { toggleStatus } from "../../features/units/utils";
import { Link } from "react-router-dom";
import { Apartment } from "@mui/icons-material";

const UnitGrid = ({ property }) => {
  const [unitsData, setUnitsData] = useState({
    ids: [],
    entities: {}, // Initialize entities as an object
  });

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const {
    data: units,
    isLoading: isUnitsLoading,
    isSuccess: isUnitsSuccess,
    refetch,
  } = useGetUnitsQuery(property?.id, { skip: !property?.id });

  const {
    // Getting data and renaming it to fetchedUnitTypes
    data: fetchedUnitTypes,
    isLoading: isUnitTypeLoading,
    isSuccess: isUnitTypeSuccess,
    isError: isUnitTypeError,
    error: unitTypeError,
  } = useGetUnitTypesByPropertyQuery(property.id, { skip: !property.id });

  useEffect(() => {
    // Trigger a refetch when this page is navigated to after property edit
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (isUnitsSuccess && units) {
      setUnitsData(units);
    }
  }, [isUnitsSuccess, units]);

  // Close snackbars
  const handleSnackbarClose = useCallback(() => {
    setSnackbarOpen(false);
    setSnackbarMessage("");
  }, []);

  // Update unit details
  const [updateUnit] = useUpdateUnitMutation();

  // Delete unit
  const [deleteUnit] = useDeleteUnitMutation();

  // Toggle unit status
  const [toggleUnitStatus, { isLoading: isUnitStatusUpdating }] =
    useToggleStatusMutation();

  // const toggleStatus = (status) =>
  //   status === "Available" ? "Occupied" : "Available";

  // Toggle Availability Handler
  const handleToggleUnitStatus = async (unitId) => {
    const originalUnitStatus = unitsData.entities[unitId].status;

    try {
      // 1. Optimistic UI update
      setUnitsData((prev) => ({
        ...prev,
        entities: {
          ...prev.entities,
          [unitId]: {
            ...prev.entities[unitId],
            status: toggleStatus(prev.entities[unitId].status),
            isUpdating: true, // Added loading state
          },
        },
      }));

      // 2. Update status
      const response = await toggleUnitStatus({
        id: unitId,
        status: toggleStatus(originalUnitStatus), // Toggle status
      }).unwrap();

      // Update with final status and clear loading
      setUnitsData((prev) => ({
        ...prev,
        entities: {
          ...prev.entities,
          [unitId]: {
            ...prev.entities[unitId],
            isUpdating: false,
          },
        },
      }));

      setSnackbarMessage(
        response.message || "Unit status updated successfully!",
      );
      setSnackbarOpen(true);
    } catch (error) {
      // 3. Rollback to original status on error
      setUnitsData((prev) => ({
        ...prev,
        entities: {
          ...prev.entities,
          [unitId]: {
            ...prev.entities[unitId],
            status: originalUnitStatus,
            isUpdating: false,
          },
        },
      }));

      console.error("Status update failed:", error);
      setSnackbarMessage(
        error.data?.message || "Failed to update unit status.",
      );
      setSnackbarOpen(true);
    }
  };

  // Update unit details on Edit
  const saveUnit = async (
    updatedData,
    deletedImageIds = [],
    originalMainImageId,
  ) => {
    const formData = new FormData();

    formData.append("id", updatedData.id);
    formData.append("prefix", updatedData.prefix);
    formData.append("number", updatedData.number);
    formData.append("unitType", updatedData.unitType);
    formData.append("size", updatedData.size);
    formData.append("bedrooms", updatedData.bedrooms);
    formData.append("bathrooms", updatedData.bathrooms);
    formData.append("price", updatedData.price);
    formData.append("paymentFrequency", updatedData.paymentFrequency);
    formData.append(
      "availabilityDates",
      JSON.stringify(updatedData.availabilityDates),
    );
    formData.append("comments", updatedData.comments);

    // Main image handling
    let mainImageAction = "keep";

    // Determine main image change type
    if (updatedData.mainImage?.file instanceof File) {
      // New mainImage to be uploaded
      formData.append("mainImage", updatedData.mainImage.file);
      mainImageAction = "new";
    } else if (
      updatedData.mainImage._id &&
      updatedData.mainImage._id !== originalMainImageId
    ) {
      // Existing image promoted to main
      mainImageAction = "changed";
    }

    (updatedData.images || []).forEach((image) => {
      if (image.file instanceof File) {
        // New images to be uploaded
        formData.append("images", image.file);
      }
    });

    // Core unit data with main image info
    formData.append(
      "unitImageData",
      JSON.stringify({
        deletedImageIds,
        keepImageIds:
          updatedData.images?.filter((img) => img._id)?.map((img) => img._id) ||
          [],
        mainImageAction,
        mainImageId: updatedData.mainImage._id,
        originalMainImageId,
      }),
    );

    // for (let [key, value] of formData.entries()) {
    //   console.log(`${key}:`, value);
    // }
    // return;

    try {
      const response = await updateUnit(formData).unwrap();
      return response;
    } catch (error) {
      console.error("Save failed:", error);
      throw error;
    }
  };

  // Edit Unit Handler
  const handleEditUnit = async (unitId, updatedData) => {
    // Original data before any modifications
    const originalUnit = unitsData.entities[unitId];

    try {
      // Track main image changes
      const originalMainImageId = originalUnit.mainImage?._id;
      // const newMainImageId = updatedData.mainImage?._id;
      // const mainImageChanged = originalMainImageId !== newMainImageId;

      // Calculate deleted images
      const originalImageIds = originalUnit.images?.map((img) => img._id) || [];
      const updatedImageIds = updatedData.images?.map((img) => img._id) || [];
      const deletedImageIds = originalImageIds.filter(
        (id) => !updatedImageIds.includes(id),
      );

      // 1. Optimistic UI update
      setUnitsData((prev) => ({
        ...prev,
        entities: {
          ...prev.entities,
          [unitId]: updatedData,
        },
      }));

      // 2. Save updated unit data
      const result = await saveUnit(
        updatedData,
        deletedImageIds,
        originalMainImageId,
      );

      setSnackbarMessage(result.message || "Unit updated successfully.");
      setSnackbarOpen(true);
    } catch (error) {
      // 3. Rollback on error
      setUnitsData((prev) => ({
        ...prev,
        entities: {
          ...prev.entities,
          [unitId]: originalUnit, // Revert to original data
        },
      }));

      console.error("Update failed:", error);
      setSnackbarMessage(error.data?.message || "Failed to update unit.");
      setSnackbarOpen(true);
    }
  };

  // Delete Unit Handler
  const handleDeleteUnit = async (unitId) => {
    // 1. Capture original state before modification
    const originalEntities = { ...unitsData.entities };
    const originalIds = [...unitsData.ids];

    try {
      setUnitsData((prev) => ({
        ids: prev.ids.filter((id) => id !== unitId),
        entities: Object.fromEntries(
          Object.entries(prev.entities).filter(([id]) => id !== unitId),
        ),
      }));

      // 2. Delete unit
      const response = await deleteUnit({ id: unitId }).unwrap();

      setSnackbarMessage(response.message || "Unit deleted successfully.");
      setSnackbarOpen(true);
    } catch (error) {
      // 3. Rollback original state on error
      setUnitsData({
        ids: originalIds,
        entities: originalEntities,
      });

      console.error("Update failed:", error);
      setSnackbarMessage(error.data?.message || "Failed to delete unit.");
      setSnackbarOpen(true);
    }
  };

  return (
    <>
      {isUnitsLoading ? (
        <Spinner />
      ) : (
        <>
          {unitsData.ids.length !== 0 ? (
            <>
              <Container sx={{ marginBottom: 2 }}>
                {/* <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" }, // Column for small screens, row for larger
                    alignItems: { xs: "center", sm: "center" }, // Center-align for small screens
                    justifyContent: { sm: "space-between" }, // Space-between for larger screens
                    gap: { xs: 2, sm: 0 }, // Add vertical spacing between buttons on small screens
                    textAlign: { xs: "center", sm: "left" }, // Center-align text on small screens
                  }}
                > */}
                <Box>
                  <Typography
                    variant="h5"
                    component="h2"
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    {/* {property.location}, {property.city}, {property.country} */}
                    <Apartment color="action" /> {property.propertyType.name}
                  </Typography>
                  <Typography variant="h6" component="p" color="text.secondary">
                    Available Room{unitsData.ids.length > 1 && "s"}
                  </Typography>
                </Box>
                {/* {property.nftType !== MONO_NFT_TYPE && (
                <Box
                  sx={{
                    display: "flex",
                    gap: 1, // Spacing between buttons
                    // flexDirection: { xs: "column", sm: "row" }, // Stack vertically on small screens
                    // width: { xs: "100%", sm: "auto" }, // Ensure buttons are full-width on small screens
                  }}
                >
                  <Button
                    variant="contained"
                    color="info"
                    size="large"
                    // onClick={handleEdit}
                  >
                    Edit Units
                  </Button>
                </Box>
              )} */}
                {/* </Box> */}
              </Container>

              <Container sx={{ marginBottom: 5 }}>
                <Grid2 container spacing={3}>
                  {/* {isUnitTypeLoading ? (
                <Grid2 size={{ xs: 12 }}>
                  <Spinner />
                </Grid2>
              ) : ( */}
                  {unitsData.ids.map((id) => (
                    // <>
                    // {/* --- LEFT COLUMN (DETAILS) --- */}
                    <Grid2 key={id} size={{ xs: 12, sm: 6, md: 4 }}>
                      <UnitCard
                        // key={id}
                        unit={unitsData.entities[id]} // Access unit details via ID
                        property={property}
                        unitTypes={fetchedUnitTypes}
                        onToggle={() => handleToggleUnitStatus(id)}
                        onEdit={(updatedUnit) =>
                          handleEditUnit(id, updatedUnit)
                        }
                        onDelete={() => handleDeleteUnit(id)}
                        // onStatusUpdating={isUnitStatusUpdating}
                      />
                    </Grid2>

                    // {/* <Grid2 size={{ md: 4 }}></Grid2> */}
                    // {/* --- RIGHT COLUMN (STICKY SIDEBAR) --- */}
                    // {/* <Grid2 size={{ xs: 12, md: 4 }}>
                    //   <Box sx={{ position: "sticky", top: 20 }}>
                    //     <RentCard
                    //       key={id}
                    //       unit={unitsData.entities[id]} // Access unit details via ID
                    //       property={property}
                    //       unitTypes={fetchedUnitTypes}
                    //     />
                    //   </Box>
                    // </Grid2> */}
                    // </>
                  ))}
                </Grid2>
              </Container>

              {/* Snackbar for image count feedback */}
              <Snackbar
                open={snackbarOpen}
                onClose={handleSnackbarClose}
                message={snackbarMessage}
                autoHideDuration={5000}
                action={
                  <IconButton
                    size="small"
                    aria-label="close"
                    color="inherit"
                    onClick={handleSnackbarClose}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                }
                // ContentProps={{
                //   sx: {
                //     backgroundColor: "#fff",
                //     color: "#000",
                //     boxShadow: 3,
                //     border: "1px solid #ccc",
                //   },
                // }}
              />
            </>
          ) : (
            <Container sx={{ marginBottom: 5 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  // minHeight: "50vh",
                  textAlign: "center",
                  p: 4,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                }}
              >
                {/* <Box sx={{ fontSize: 40, color: "text.secondary" }}>
                  <AddCircleOutlineIcon fontSize="inherit" />
                </Box> */}
                <Typography variant="h5" gutterBottom>
                  No Units Available
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Get started by adding your first property unit
                </Typography>
                <Link to={`../../my-units/new?id=${property.id}`}>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="large"
                    startIcon={<AddIcon />}
                  >
                    Add Unit
                  </Button>
                </Link>
              </Box>
            </Container>
          )}
        </>
      )}
    </>
  );
};

export default UnitGrid;
